// The bar that floats over the bottom of the grid while something is selected
// (Leon, 2026-10-23, mockup v13): "7 bookings", one total per money column
// (each one click to copy — never one sum across different columns), then
// Copy, Export, Show only these and clear.

import { Button } from "@bednbody/ui/components/button";
import { CopyIcon, DownloadSimpleIcon, EyeIcon, XIcon } from "@phosphor-icons/react";

export interface SheetSelectionBarProps {
  label: string;
  totals: readonly { key: string; label: string; text: string; raw: string }[];
  onCopyTotal: (label: string, raw: string, text: string) => void;
  onCopy: () => void;
  onExport: () => void;
  /** Only for a selection of rows, not a box of cells. */
  onShowOnly: (() => void) | null;
  onClear: () => void;
}

export function SheetSelectionBar({
  label,
  totals,
  onCopyTotal,
  onCopy,
  onExport,
  onShowOnly,
  onClear,
}: SheetSelectionBarProps) {
  return (
    <section
      aria-label="Selection"
      className="absolute bottom-12 left-1/2 z-40 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-1.5 overflow-x-auto rounded-2xl bg-foreground py-1.5 pr-1.5 pl-4 text-background shadow-float animate-in fade-in-0 slide-in-from-bottom-2"
    >
      <strong className="shrink-0 text-sm whitespace-nowrap">{label}</strong>
      {totals.map((total) => (
        <button
          className="flex shrink-0 items-baseline gap-1.5 rounded-lg bg-background/10 px-2.5 py-1.5 text-sm whitespace-nowrap hover:bg-background/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background"
          key={total.key}
          onClick={() => onCopyTotal(total.label, total.raw, total.text)}
          title={`Copy the ${total.label} total`}
          type="button"
        >
          <span className="opacity-70">{total.label}</span>
          <span className="font-mono font-semibold tabular-nums">{total.text}</span>
        </button>
      ))}
      <span aria-hidden="true" className="mx-1 h-5 w-px shrink-0 bg-background/25" />
      <Button className="shrink-0" onClick={onCopy} size="sm" type="button" variant="secondary">
        <CopyIcon /> Copy
      </Button>
      <Button
        className="shrink-0 border-background/30 bg-transparent text-background hover:bg-background/10 hover:text-background"
        onClick={onExport}
        size="sm"
        type="button"
        variant="outline"
      >
        <DownloadSimpleIcon /> Export
      </Button>
      {onShowOnly ? (
        <Button
          className="shrink-0 border-background/30 bg-transparent text-background hover:bg-background/10 hover:text-background"
          onClick={onShowOnly}
          size="sm"
          type="button"
          variant="outline"
        >
          <EyeIcon /> Show only these
        </Button>
      ) : null}
      <Button
        aria-label="Clear selection"
        className="shrink-0 text-background hover:bg-background/10 hover:text-background"
        onClick={onClear}
        size="icon-sm"
        type="button"
        variant="ghost"
      >
        <XIcon />
      </Button>
    </section>
  );
}
